/**
 * Generates an alphanumeric state key and stores it in sessionStorage with a 10-minute expiration
 * The state key is stored with an embedded timestamp for expiration checking
 * @returns The generated state key
 */
export const generateStateKey = (): string => {
  clearStoredStateKey();
  
  // Generate a random alphanumeric string of 32 characters
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let stateKey = '';
  
  for (let i = 0; i < 32; i++) {
    stateKey += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  // Calculate expiration time (10 minutes from now)
  const expirationTime = new Date().getTime() + (10 * 60 * 1000); // 10 minutes in milliseconds

  // Store the state key with its expiration time as a single entry
  const stateWithExpiration = JSON.stringify({
    key: stateKey,
    expiration: expirationTime
  });
  
  sessionStorage.setItem('stateKey', btoa(stateWithExpiration)); // Store as base64 encoded JSON
  
  return stateKey;
};

/**
 * Retrieves the stored state key from sessionStorage if it's still valid
 * @returns The stored state key if valid, null otherwise
 */
export const getStoredStateKey = (): string | null => {
  const encodedStateData = sessionStorage.getItem('stateKey');
  
  // If no encoded state data exists, return null
  if (!encodedStateData) {
    return null;
  }
  
  try {
    // Decode the base64 encoded JSON data
    const stateData = JSON.parse(atob(encodedStateData));
    
    // Check if the state key has expired
    const currentTime = new Date().getTime();
    if (currentTime > stateData.expiration) {
      // State key has expired, remove it from sessionStorage
      clearStoredStateKey();
      return null;
    }
    
    return stateData.key;
  } catch (e) {
    // If there's an error parsing the data, remove it and return null
    clearStoredStateKey();
    return null;
  }
};

/**
 * Removes the stored state key from sessionStorage
 */
export const clearStoredStateKey = (): void => {
  sessionStorage.removeItem('stateKey');
};